import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { 
  Home, 
  Users, 
  UserCheck, 
  Clock, 
  FileText, 
  LogOut,
  Menu,
  X,
  User,
  ClipboardList,
  ChevronRight,
  ChevronDown,
  Bell,
  CreditCard,
  Receipt,
  BarChart3,
  Link2,
  MessageSquare,
  Camera,
  Eye,
  Building2,
  Database
} from "lucide-react";

export default function Sidebar() {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [adminUnreadReplies, setAdminUnreadReplies] = useState(0);

  // Fetch pending registrants count for admin/property manager
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!token || (user?.role !== "admin" && user?.role !== "manager" && user?.role !== "property_manager")) {
        return;
      }
      try {
        const response = await axios.get(`${API}/admin/pending-approval`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingCount(response.data?.length || 0);
      } catch (error) {
        console.error("Failed to fetch pending count:", error);
      }
    };

    fetchPendingCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  // Fetch unread messages count for tenants
  useEffect(() => {
    const fetchUnreadMessages = async () => {
      if (!token || user?.user_type !== "tenant") {
        return;
      }
      try {
        const response = await axios.get(`${API}/messages/unread/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadMessages(response.data?.count || 0);
      } catch (error) {
        console.error("Failed to fetch unread messages:", error);
      }
    };

    fetchUnreadMessages();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadMessages, 30000);
    return () => clearInterval(interval);
  }, [token, user?.user_type]);

  // Fetch unread replies count for admin
  useEffect(() => {
    const fetchAdminUnreadReplies = async () => {
      if (!token || user?.role !== "admin") {
        return;
      }
      try {
        const response = await axios.get(`${API}/admin/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminUnreadReplies(response.data?.count || 0);
      } catch (error) {
        console.error("Failed to fetch admin unread replies:", error);
      }
    };

    fetchAdminUnreadReplies();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchAdminUnreadReplies, 30000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  const getProfilePhotoUrl = () => {
    if (user?.profile_photo) {
      if (user.profile_photo.startsWith("http")) {
        return user.profile_photo;
      }
      return `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`;
    }
    return null;
  };

  // Links for regular users (applicants)
  const applicantLinks = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/new-application", label: "New Tenant Application", icon: FileText },
  ];

  // Links for existing tenants
  const tenantLinks = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/tenant-info-update", label: "Update My Information", icon: ClipboardList },
    { path: "/payment-portal", label: "Payment Portal", icon: CreditCard },
    { path: "/messages", label: "Messages", icon: MessageSquare },
  ];

  // Links for property managers (limited access)
  const propertyManagerLinks = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/admin/registrants", label: "Registrants", icon: Users },
    { path: "/admin/tenants", label: "Tenants", icon: UserCheck },
    { path: "/admin/pending-approval", label: "Pending Approval", icon: Clock },
    { path: "/admin/tenant-info", label: "Tenant Info Update", icon: ClipboardList },
    { path: "/admin/payment-confirmations", label: "Payment Confirmations", icon: Receipt },
    { path: "/admin/messages", label: "Messages", icon: MessageSquare },
  ];

  // Links for admin (full access) - Form View is handled separately as collapsible
  const adminLinks = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/admin/registrants", label: "Registrants", icon: Users },
    { path: "/admin/tenants", label: "Tenants", icon: UserCheck },
    { path: "/admin/pending-approval", label: "Pending Approval", icon: Clock },
    { path: "/admin/application-links", label: "Application Links", icon: Link2 },
    { path: "/admin/tenant-info", label: "Tenant Info Update", icon: ClipboardList },
    { path: "/admin/payment-confirmations", label: "Payment Confirmations", icon: Receipt },
    { path: "/admin/payment-reminders", label: "Payment Reminders", icon: Bell },
    { path: "/admin/reports", label: "Reports", icon: BarChart3 },
    { path: "/admin/messages", label: "Messages", icon: MessageSquare },
    { path: "/admin/security-cameras", label: "Security Cameras", icon: Camera },
  ];

  // Form View sub-items (admin only)
  const formViewSubItems = [
    { path: "/admin/form-view", label: "Tenant Info Update", icon: ClipboardList },
    { path: "/admin/form-view/application", label: "New Tenant Application", icon: FileText },
  ];

  // Properties sub-items (admin only)
  const propertiesSubItems = [
    { path: "/admin/properties", label: "Property List", icon: Building2 },
    { path: "/admin/tenant-database", label: "Tenant Database", icon: Database },
  ];

  // State for collapsible Form View menu
  const [formViewOpen, setFormViewOpen] = useState(false);

  // State for collapsible Properties menu
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  // Auto-expand Form View if we're on one of its sub-pages
  useEffect(() => {
    if (location.pathname.startsWith("/admin/form-view")) {
      setFormViewOpen(true);
    }
  }, [location.pathname]);

  // Auto-expand Properties if we're on one of its sub-pages
  useEffect(() => {
    if (location.pathname.startsWith("/admin/properties") || location.pathname.startsWith("/admin/tenant-database")) {
      setPropertiesOpen(true);
    }
  }, [location.pathname]);

  // Determine which links to show based on user role and type
  const getLinks = () => {
    if (user?.role === "admin") {
      return adminLinks;
    }
    if (user?.role === "manager" || user?.role === "property_manager") {
      return propertyManagerLinks;
    }
    if (user?.user_type === "tenant") {
      return tenantLinks;
    }
    return applicantLinks;
  };

  const links = getLinks();

  const getRoleDisplay = () => {
    if (user?.role === "admin") return "Admin";
    if (user?.role === "manager" || user?.role === "property_manager") return "Property Manager";
    if (user?.user_type === "tenant") return "Tenant";
    if (user?.user_type === "applicant") return "Applicant";
    return "User";
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-stone-200 z-30 md:hidden flex items-center px-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-xl bg-stone-100 hover:bg-stone-200 transition-colors mr-3"
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X className="w-6 h-6 text-primary" /> : <Menu className="w-6 h-6 text-primary" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-primary">WilyRash Homes</span>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-72 bg-white border-r border-stone-200 z-50 transform transition-transform duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:w-64 md:z-40`}
        data-testid="sidebar"
      >
        <div className="flex flex-col min-h-full">
          {/* Logo - Hidden on mobile (shown in header bar) */}
          <div className="p-6 border-b border-stone-200 hidden md:block flex-shrink-0">
            <Link to="/dashboard" onClick={() => setIsOpen(false)} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-primary text-lg">WilyRash</h1>
                <p className="text-xs text-text-muted">Homes</p>
              </div>
            </Link>
          </div>

          {/* Mobile Logo with Close Button */}
          <div className="p-4 border-b border-stone-200 md:hidden flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-primary text-lg">WilyRash</h1>
                <p className="text-xs text-text-muted">Homes</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-stone-100 transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-stone-200 flex-shrink-0">
            <Link 
              to="/profile" 
              onClick={() => setIsOpen(false)} 
              className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-primary/20 flex-shrink-0">
                {getProfilePhotoUrl() ? (
                  <img 
                    src={getProfilePhotoUrl()} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary font-semibold text-lg">
                    {user?.full_name?.charAt(0) || user?.username?.charAt(0) || "U"}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name || user?.username}</p>
                <p className="text-xs text-text-muted capitalize">{getRoleDisplay()}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
            </Link>
          </div>

          {/* Navigation - Main Menu */}
          <nav className="flex-1 p-3">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider px-3 mb-2">Menu</p>
            <div className="space-y-1">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive(link.path)
                      ? 'bg-primary text-white font-medium shadow-md'
                      : 'text-stone-600 hover:bg-stone-100 active:bg-stone-200'
                  }`}
                  data-testid={`sidebar-link-${link.path.replace(/\//g, '-')}`}
                >
                  <link.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm flex-1">{link.label}</span>
                  {/* Show badge for Registrants with pending count */}
                  {link.path === "/admin/registrants" && pendingCount > 0 && (
                    <Badge 
                      className={`ml-auto text-xs px-2 py-0.5 ${
                        isActive(link.path) 
                          ? 'bg-white text-primary' 
                          : 'bg-red-500 text-white'
                      }`}
                      data-testid="pending-registrants-badge"
                    >
                      {pendingCount}
                    </Badge>
                  )}
                  {/* Show badge for Tenant Messages with unread count */}
                  {link.path === "/messages" && unreadMessages > 0 && (
                    <Badge 
                      className={`ml-auto text-xs px-2 py-0.5 ${
                        isActive(link.path) 
                          ? 'bg-white text-primary' 
                          : 'bg-red-500 text-white'
                      }`}
                      data-testid="unread-messages-badge"
                    >
                      {unreadMessages}
                    </Badge>
                  )}
                  {/* Show badge for Admin Messages with unread replies */}
                  {link.path === "/admin/messages" && adminUnreadReplies > 0 && (
                    <Badge 
                      className={`ml-auto text-xs px-2 py-0.5 ${
                        isActive(link.path) 
                          ? 'bg-white text-primary' 
                          : 'bg-red-500 text-white'
                      }`}
                      data-testid="admin-unread-replies-badge"
                    >
                      {adminUnreadReplies}
                    </Badge>
                  )}
                </Link>
              ))}

              {/* Collapsible Form View - Admin Only */}
              {user?.role === "admin" && (
                <div className="mt-1">
                  <button
                    onClick={() => setFormViewOpen(!formViewOpen)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      location.pathname.startsWith("/admin/form-view")
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-stone-600 hover:bg-stone-100 active:bg-stone-200'
                    }`}
                    data-testid="form-view-toggle"
                  >
                    <Eye className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm flex-1 text-left">Form View</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${formViewOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Sub-items */}
                  {formViewOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-stone-200 pl-2">
                      {formViewSubItems.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                            isActive(subItem.path)
                              ? 'bg-primary text-white font-medium shadow-sm'
                              : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                          }`}
                          data-testid={`sidebar-link-${subItem.path.replace(/\//g, '-')}`}
                        >
                          <subItem.icon className="w-4 h-4 flex-shrink-0" />
                          <span>{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Collapsible Properties - Admin Only */}
              {user?.role === "admin" && (
                <div className="mt-1">
                  <button
                    onClick={() => setPropertiesOpen(!propertiesOpen)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      location.pathname.startsWith("/admin/properties") || location.pathname.startsWith("/admin/tenant-database")
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-stone-600 hover:bg-stone-100 active:bg-stone-200'
                    }`}
                    data-testid="properties-toggle"
                  >
                    <Building2 className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm flex-1 text-left">Properties</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${propertiesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Sub-items */}
                  {propertiesOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-stone-200 pl-2">
                      {propertiesSubItems.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setIsOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                            isActive(subItem.path)
                              ? 'bg-primary text-white font-medium shadow-sm'
                              : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                          }`}
                          data-testid={`sidebar-link-${subItem.path.replace(/\//g, '-')}`}
                        >
                          <subItem.icon className="w-4 h-4 flex-shrink-0" />
                          <span>{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>

          {/* Bottom Section - Account */}
          <div className="p-3 border-t border-stone-200 flex-shrink-0 bg-white">
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider px-3 mb-2">Account</p>
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all mb-1 ${
                isActive("/profile")
                  ? 'bg-primary text-white font-medium shadow-md'
                  : 'text-stone-600 hover:bg-stone-100 active:bg-stone-200'
              }`}
              data-testid="sidebar-profile-link"
            >
              <User className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">User Profile</span>
            </Link>
            <Button
              variant="ghost"
              onClick={() => {
                setIsOpen(false);
                handleLogout();
              }}
              className="w-full justify-start gap-3 px-4 py-3 h-auto text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Logout</span>
            </Button>
          </div>
          
          {/* Safe area padding for mobile devices with notches */}
          <div className="h-6 flex-shrink-0 md:hidden"></div>
        </div>
      </aside>
    </>
  );
}
